import {
  MessageFactory,
  type MessageConfig,
  type MessageInstance,
} from '../features/message/factory';

// Message Hook 返回值类型
export interface UseMessageReturn {
  // 显示不同类型的消息
  success: (config: Omit<MessageConfig, 'variant'> | string) => MessageInstance;
  error: (config: Omit<MessageConfig, 'variant'> | string) => MessageInstance;
  warning: (config: Omit<MessageConfig, 'variant'> | string) => MessageInstance;
  info: (config: Omit<MessageConfig, 'variant'> | string) => MessageInstance;
  // 通用创建方法
  create: (config: MessageConfig) => MessageInstance;
  // 清除方法
  clear: () => void;
}

/**
 * 消息通知 Hook
 * 提供简洁的 API 来显示各种类型的消息通知
 */
export function useMessage(): UseMessageReturn {
  // 处理字符串或配置对象参数
  const normalizeConfig = (
    input: string | Omit<MessageConfig, 'variant'>,
  ): Omit<MessageConfig, 'variant'> => {
    if (typeof input === 'string') {
      return { content: input };
    }
    return input;
  };

  return {
    success: config => {
      const normalizedConfig = normalizeConfig(config);
      return MessageFactory.success(normalizedConfig);
    },

    error: config => {
      const normalizedConfig = normalizeConfig(config);
      return MessageFactory.error(normalizedConfig);
    },

    warning: config => {
      const normalizedConfig = normalizeConfig(config);
      return MessageFactory.warning(normalizedConfig);
    },

    info: config => {
      const normalizedConfig = normalizeConfig(config);
      return MessageFactory.info(normalizedConfig);
    },

    create: config => {
      return MessageFactory.create(config);
    },

    clear: () => {
      MessageFactory.clear();
    },
  };
}

// 全局消息实例，可以在任何地方使用
export const message = {
  success: (config: string | Omit<MessageConfig, 'variant'>) => {
    const normalizedConfig = typeof config === 'string' ? { title: config } : config;
    return MessageFactory.success(normalizedConfig);
  },

  error: (config: string | Omit<MessageConfig, 'variant'>) => {
    const normalizedConfig = typeof config === 'string' ? { title: config } : config;
    return MessageFactory.error(normalizedConfig);
  },

  warning: (config: string | Omit<MessageConfig, 'variant'>) => {
    const normalizedConfig = typeof config === 'string' ? { title: config } : config;
    return MessageFactory.warning(normalizedConfig);
  },

  info: (config: string | Omit<MessageConfig, 'variant'>) => {
    const normalizedConfig = typeof config === 'string' ? { title: config } : config;
    return MessageFactory.info(normalizedConfig);
  },

  create: (config: MessageConfig) => MessageFactory.create(config),
  clear: () => MessageFactory.clear(),
};

// 导出类型
export type { MessageConfig, MessageInstance } from '../features/message/factory';
